import React, { useMemo } from "react";

import {
  faInbox,
  faTriangleExclamation,
} from "@fortawesome/pro-solid-svg-icons";

import { Flow } from "src/api/flowTypes";
import { useFlows } from "src/api/queries";
import { WorkspaceDataplane } from "src/clients/flow-api";
import { EmptyState } from "src/design-system/EmptyState";
import { Skeleton } from "src/design-system/Skeleton";
import { useAllJobs } from "src/jobs/api/queries";
import { Job, JobConfiguration } from "src/jobs/types";
import { getUrlToJobPage } from "src/router/urls";

type Props = {
  workspace: WorkspaceDataplane;
};

type JobWithFlow = Job & {
  flowName: string;
  flowSlug: string;
};

const formatConfiguration = (config?: JobConfiguration): string => {
  if (!config) {
    return "Default (1 concurrent, async)";
  }

  if ("execution_concurrency" in config && config.execution_concurrency) {
    return `Concurrency: ${config.execution_concurrency}, Mode: ${config.execution_mode || "async"}`;
  }

  if ("rate_limit" in config && config.rate_limit) {
    return `Rate Limit: ${config.rate_limit}/${config.rate_limit_interval || "SECOND"}, Mode: ${config.execution_mode || "async"}`;
  }

  return "Custom configuration";
};

const formatSchedule = (
  schedule?: string | null,
  timezone?: string | null,
): string => {
  if (!schedule) {
    return "None (manual only)";
  }
  return timezone ? `${schedule} (${timezone})` : schedule;
};

const JobConfigDisplay: React.FC<{ config?: JobConfiguration }> = ({
  config,
}) => {
  const formatted = useMemo(() => formatConfiguration(config), [config]);

  if (!config) {
    return <span className="text-gray-500">{formatted}</span>;
  }

  return (
    <details className="cursor-pointer">
      <summary className="text-indigo-600 hover:text-indigo-800">
        {formatted}
      </summary>
      <pre className="mt-2 overflow-x-auto rounded bg-gray-50 p-2 text-xs">
        {JSON.stringify(config, null, 2)}
      </pre>
    </details>
  );
};

const JobsTable: React.FC<{
  jobs: JobWithFlow[];
  orgId: string;
  wsId: string;
}> = ({ jobs, orgId, wsId }) => {
  return (
    <div className="overflow-x-auto">
      <table className="min-w-full border-collapse font-inter-normal-12px text-gray-500">
        <thead>
          <tr className="border-gray-200 border-b bg-gray-50">
            <th className="px-3 py-2 text-left font-inter-semibold-12px text-gray-700">
              Job Name
            </th>
            <th className="px-3 py-2 text-left font-inter-semibold-12px text-gray-700">
              Status
            </th>
            <th className="px-3 py-2 text-left font-inter-semibold-12px text-gray-700">
              Flow
            </th>
            <th className="px-3 py-2 text-left font-inter-semibold-12px text-gray-700">
              Schedule
            </th>
            <th className="px-3 py-2 text-left font-inter-semibold-12px text-gray-700">
              Configuration
            </th>
            <th className="px-3 py-2 text-left font-inter-semibold-12px text-gray-700">
              Runs (Live/Sandbox)
            </th>
          </tr>
        </thead>
        <tbody>
          {jobs.map((job) => {
            const jobUrl = getUrlToJobPage(orgId, wsId, job.flow_id, job.id);
            return (
              <tr key={job.id} className="border-gray-100 border-b">
                <td className="px-3 py-2">
                  <a
                    className="text-indigo-600 hover:text-indigo-800 hover:underline"
                    href={`/decide${jobUrl}`}
                  >
                    {job.name}
                  </a>
                </td>
                <td className="px-3 py-2">
                  <span
                    className={`inline-block rounded px-1.5 py-0.5 text-xs ${
                      job.status === "active"
                        ? "bg-green-100 text-green-800"
                        : "bg-gray-100 text-gray-600"
                    }`}
                  >
                    {job.status}
                  </span>
                </td>
                <td className="px-3 py-2">{job.flowName}</td>
                <td className="px-3 py-2">
                  {formatSchedule(job.schedule, job.schedule_timezone)}
                </td>
                <td className="px-3 py-2">
                  <JobConfigDisplay config={job.configuration} />
                </td>
                <td className="px-3 py-2">
                  {job.live_runs_count || 0} / {job.sandbox_runs_count || 0}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export const JobsDebugSection: React.FC<Props> = ({ workspace }) => {
  const flowsQuery = useFlows({
    workspaceId: workspace.id,
    folderId: undefined,
    disabled: false,
  });

  const jobsQuery = useAllJobs(workspace.base_url ?? "");

  const allJobsWithFlows = useMemo<JobWithFlow[]>(() => {
    if (!jobsQuery.data?.length) return [];

    const flowsById = new Map<string, Flow>(
      (flowsQuery.data ?? []).map((flow: Flow) => [flow.id, flow]),
    );

    return jobsQuery.data.map((job: Job) => {
      const flow = flowsById.get(job.flow_id);
      return {
        ...job,
        flowName: flow?.name || "Unknown Flow",
        flowSlug: flow?.slug || "",
      };
    });
  }, [jobsQuery.data, flowsQuery.data]);

  const activeJobs = useMemo(
    () => allJobsWithFlows.filter((job) => job.status === "active"),
    [allJobsWithFlows],
  );

  const inactiveJobs = useMemo(
    () => allJobsWithFlows.filter((job) => job.status === "inactive"),
    [allJobsWithFlows],
  );

  if (
    (!flowsQuery.isFullyLoaded && !flowsQuery.isError) ||
    (!jobsQuery.isFullyLoaded && !jobsQuery.isError)
  ) {
    return <Skeleton height="h-3" />;
  }

  if (flowsQuery.isError || jobsQuery.isError) {
    return (
      <EmptyState
        description="Try again in a moment or check the workspace connection"
        headline="Error loading jobs"
        icon={faTriangleExclamation}
        size="compact"
        variant="error"
      />
    );
  }

  if (allJobsWithFlows.length === 0) {
    return (
      <EmptyState
        headline="No jobs found in this workspace"
        icon={faInbox}
        size="compact"
      />
    );
  }

  return (
    <div>
      <h1>Jobs ({allJobsWithFlows.length} total)</h1>

      {activeJobs.length > 0 && (
        <>
          <h2 className="mt-4 mb-3 font-inter-semibold-14px text-gray-800">
            Active Jobs ({activeJobs.length})
          </h2>
          <JobsTable
            jobs={activeJobs}
            orgId={workspace.organization_id}
            wsId={workspace.id}
          />
        </>
      )}

      {inactiveJobs.length > 0 && (
        <>
          <h2 className="mt-4 mb-3 font-inter-semibold-14px text-gray-800">
            Inactive Jobs ({inactiveJobs.length})
          </h2>
          <JobsTable
            jobs={inactiveJobs}
            orgId={workspace.organization_id}
            wsId={workspace.id}
          />
        </>
      )}
    </div>
  );
};
